import {
  computeProportionAtTextPosition,
  DEFAULT_SPLIT_STRATEGY,
  tokenizePhraseWords,
} from '@src/editor/services/karaokeWordTiming';
import { useKaraoke } from '@src/hooks/useKaraoke';
import {
  useEditor,
  useTranscription,
  useTranslationStore,
} from '@src/stores-v2/StoreContext';
import { formatLanguageCaption } from '@src/utility/languageCaption';
import {
  needsSpaceBetweenCaptionRows,
  splitSpacelessScriptCharacters,
} from '@src/utility/strings';
import { observer } from 'mobx-react-lite';
import React from 'react';
import styled from 'styled-components';

import {
  CAPTION_LINE_DIVIDER_COLOR,
  CAPTION_LINE_FONT_SIZE,
  CAPTION_LINE_LINE_HEIGHT,
  CAPTION_LINE_VERTICAL_PADDING,
} from './captionLineStyles';
import { changedPhraseHighlightCss } from './changedPhraseKit';
import { buildPhraseMembershipMaps } from './hooks/phraseMembershipMaps';
import { usePhraseEditing } from './hooks/usePhraseEditing';
import { InlineSpanReplaceLayer } from './InlineSpanReplaceLayer';
import { TranslatedPhraseContextMenu } from './TranslatedPhraseContextMenu';
import { isEdited, resolveSourceText } from './translatedPhraseEditing';
import { TranslatedPhraseEditorPopover } from './TranslatedPhraseEditorPopover';
import { isPhraseInTranslatedSelection } from './translatedSelectionGuard';
import { computePlayheadScrollTop } from './utils';

import type { DerivedCaptionPhrase } from './hooks/useTranslatedCaptionLines';
import type { PhraseSoftBreak } from '@src/editor/services/captionSoftBreaks';
import type { KaraokeLine } from '@src/editor/types/karaoke';
import type { TranslationPhrase } from '@src/types.ts/translation';

interface SyntheticBreakHandlers {
  onAddBreak: (
    lineId: string,
    phraseOriginalIndices: number[],
    textPosition: number,
  ) => void;
  onRemoveBreak: (breakId: string) => void;
  /**
   * PLAT-9623: merge two adjacent caption clips. Pass the RIGHT
   * clip's id; the editor pulls in the left neighbor and merges.
   * Used for Backspace at first-of-line (rightClipId = current
   * line's id) and Delete at last-of-line (rightClipId = next
   * line's id). Handles both single-phrase synthetic siblings AND
   * multi-phrase clips that were split without a break record.
   */
  onMerge: (rightLineId: string) => void;
}

interface Props {
  lines: readonly KaraokeLine[];
  currentTime?: number;
  isPlaying?: boolean;
  onLineClick?: (initialIndex: number) => void;
  /**
   * PLAT-9623: invoked whenever the navigation cursor moves to a new
   * word boundary (via arrow keys, click, or first-engage default).
   * The parent uses this to seek the video to the corresponding
   * "synthetic" time so the preview frame follows the caption cursor —
   * intra-phrase positions resolve via the same SplitStrategy used by
   * synthetic breaks, so the preview lands on the same frame the user
   * would get if they pressed Enter to split there.
   */
  onSeekToTime?: (time: number) => void;
  /**
   * PLAT-9623: when present, enables line-break editing via a single
   * navigation cursor. The user clicks between any two words (or
   * focuses the panel and uses ←/→) to place the cursor, then presses
   * Enter to break the line or Delete/Backspace to merge. The cursor
   * is the same yellow blinker everywhere — phrase boundaries are not
   * visually distinguished — because internally we pick the right
   * command (synthetic vs regular). The customer never has to think
   * about which kind of break it is.
   */
  syntheticBreakHandlers?: SyntheticBreakHandlers;
  /**
   * PLAT-10457 (A3): when true (the Fix-Translation gesture is enabled), each
   * rendered caption word span is stamped with its source-phrase identity
   * (`data-initial-index` = lead integer, `data-source-uuid` = lead uuid) so
   * the A4/A5 click / selection resolver can map a span back to its phrase —
   * mirroring the prose translated view. Default false → no identity attrs.
   * This view is only mounted for a foreign-language translation (the
   * original-language captions use `KaraokeLinesView`), so gating on this flag
   * alone also satisfies "no affordance in the original language".
   */
  enableInlineSpanReplace?: boolean;
  /**
   * PLAT-10457 (A8): when true, an EDITED caption phrase's word span(s) carry the
   * `edited` class so the SHARED `changedPhraseHighlightCss` paints the green
   * "changed" background — the SAME mark the prose translated view and the
   * Translation Review modal already show. Default false → no `edited` class,
   * byte-identical to today. This closes the forwarding A3 deferred (the
   * highlight consumer landed after the A4/A5 gesture). Green MARK only — no
   * hover tooltip, no undo. The hosts drive it off `enableTranslationReview`
   * (foreign-language only), so it never lights the original language.
   */
  enableChangedHighlight?: boolean;
  /**
   * PLAT-10457 (A4): the active translation (foreign) language under edit.
   * Required by the mounted `InlineSpanReplaceLayer` — `useSpanReplace` authors
   * the override against it. This view previously had no `language` prop; it is
   * threaded from `Transcript.tsx`'s captions branch, mirroring the A3
   * flag-forward. Nullable to match the host's `language: string | null` prop;
   * the layer mounts only when this resolves to a non-empty string.
   */
  language?: string | null;
}

/**
 * PLAT-9621 — line-by-line caption view in the active translation.
 * PLAT-9623 — when paused, supports adding / removing line breaks via
 * keyboard navigation. Click between two words to place the cursor,
 * ←/→ to walk word boundaries, Enter to break, Delete to merge. The
 * cursor is one yellow color everywhere; the customer doesn't see (or
 * care about) the synthetic-vs-natural distinction.
 */
export const TranslatedCaptionsView: React.FC<Props> = observer(
  ({
    lines,
    currentTime,
    isPlaying,
    onLineClick,
    onSeekToTime,
    syntheticBreakHandlers,
    enableInlineSpanReplace = false,
    enableChangedHighlight = false,
    language,
  }) => {
    // PLAT-10457 (A5): reuse the shipped "Fix Translation" right-click menu +
    // editor popover on a SINGLE caption word — the SAME hook + components the
    // prose translated view (`TranscriptionTextTranslated`) wires, not a rebuilt
    // parallel. `editable` is the paused-only, foreign-language gate (same family
    // as A4's layer mount); when it flips off (playback starts, flag off, or no
    // language) `usePhraseEditing` force-closes any open menu/popover. `language`
    // is nullable here to match the host prop, so the hook gets the non-null
    // string (`?? ''`) while `editable` carries the real `!!language` guard.
    const translationStore = useTranslationStore();
    const transcriptionStore = useTranscription();
    // PLAT-11914: soft line breaks — Shift+Enter / right-click at the cursor.
    const { commandToggleCaptionSoftBreak } = useKaraoke(useEditor().editor);
    const toggleSoftBreakAt = React.useCallback(
      (pos: CursorPosition) => {
        // The first word of a caption already starts a line.
        if (pos.beforeWord === 0) return;
        // Removing: toggle the STORED break, which can sit off this word's
        // start — toggling the word start would add a second break.
        const at = pos.softBreakBefore ?? {
          phraseIndex: pos.phraseIndex,
          offset: pos.offsetInPhrase,
        };
        commandToggleCaptionSoftBreak({ clipId: pos.lineId, ...at });
      },
      [commandToggleCaptionSoftBreak],
    );
    const [lineBreakMenu, setLineBreakMenu] = React.useState<{
      position: CursorPosition;
      positionXY: { x: number; y: number };
    } | null>(null);
    const editable = enableInlineSpanReplace && !isPlaying && !!language;
    const {
      contextMenu,
      editing,
      editingCurrentValue,
      editingOriginalValue,
      editingIsEdited,
      handleContextMenuRequest,
      handleMenuEdit,
      handleMenuClose,
      handleSave,
      handleCancel,
      handleRevert,
    } = usePhraseEditing(language ?? '', editable);
    const translation = language
      ? translationStore.getTranslation(language)
      : null;

    // PLAT-10457 (A8): the green "changed" highlight — REUSE the shared
    // changed-phrase kit (the prose translated view + the review modal are the
    // other two consumers), never a parallel check. Compute the EDITED phrase set
    // ONCE per render (CLAUDE.md #6: transcript arrays can be thousands of words —
    // never an O(N) edited-scan per word). Reuses the SHARED membership resolver
    // (`buildPhraseMembershipMaps`, uuid-only — the SAME join
    // the A5 right-click uses) and the SHARED `isEdited` predicate. Returns a
    // per-word O(1) predicate, or null when the flag is off / nothing is edited
    // (no word gets the `edited` class → byte-identical to today). A word whose
    // derived phrase doesn't resolve to a store phrase is simply not marked
    // (guard, no throw). Reactive: `editTranslatedPhrase` replaces the translation
    // object + phrases array, so this recomputes on every applied edit.
    const isCaptionWordEdited = React.useMemo<
      ((phrase: DerivedCaptionPhrase | null) => boolean) | null
    >(() => {
      if (!enableChangedHighlight) return null;
      const phrases = translation?.phrases ?? [];
      const { phraseByUuid } = buildPhraseMembershipMaps(phrases);
      const editedPhrases = new Set<TranslationPhrase>();
      for (const phrase of phrases) {
        if (isEdited(phrase, phrases)) editedPhrases.add(phrase);
      }
      if (editedPhrases.size === 0) return null;
      return (derived: DerivedCaptionPhrase | null): boolean => {
        if (!derived) return false;
        const storePhrase = derived.leadUuid
          ? phraseByUuid.get(derived.leadUuid)
          : undefined;
        return storePhrase !== undefined && editedPhrases.has(storePhrase);
      };
    }, [enableChangedHighlight, translation]);

    // The ONLY genuinely-new A5 logic: resolve a right-clicked caption word's
    // in-memory `DerivedCaptionPhrase` (A2's additive lead identity) back to its
    // live store `TranslationPhrase`, because the reused hook reads
    // `.originalIndices` / `.originalElementIds` / `.parentId` off a REAL store
    // phrase (the derived caption shape has none of those). Reuses A2's SHARED
    // membership builder — UUID-only, mirroring the render join — never a private
    // lookup. No resolution → no menu (guard);
    // the native browser menu is suppressed only once we commit to showing ours.
    const handleWordContextMenu = React.useCallback(
      (
        derived: DerivedCaptionPhrase | null,
        e: React.MouseEvent<HTMLElement>,
      ) => {
        if (!derived) return;
        e.preventDefault();
        e.stopPropagation();
        const phrases =
          translationStore.getTranslation(language ?? '')?.phrases ?? [];
        const { phraseByUuid } = buildPhraseMembershipMaps(phrases);
        const storePhrase = derived.leadUuid
          ? phraseByUuid.get(derived.leadUuid)
          : undefined;
        if (!storePhrase) return;
        // PLAT-10457 (A5.1): selection precedence — mirror the prose rule
        // (PLAT-10467, `TranscriptionTextTranslated`). When the span (drag-select)
        // path is enabled AND a non-empty selection already covers this word, let
        // the span editor win: suppress the single-word menu. `phrases` is the
        // same store set resolved above; `containerRef.current` scopes the
        // selection to THIS view (CLAUDE.md #13 — never a global `document`).
        // `e.preventDefault()` already ran at the top of this handler
        // (idempotent), kept here for prose parity.
        if (
          enableInlineSpanReplace &&
          isPhraseInTranslatedSelection(
            containerRef.current,
            phrases,
            storePhrase,
          )
        ) {
          e.preventDefault();
          return;
        }
        handleContextMenuRequest(storePhrase, e.currentTarget, {
          x: e.clientX,
          y: e.clientY,
        });
      },
      [
        translationStore,
        language,
        handleContextMenuRequest,
        enableInlineSpanReplace,
      ],
    );

    const currentLineId = React.useMemo(() => {
      if (typeof currentTime !== 'number') return null;
      for (const line of lines) {
        if (
          currentTime >= line.transcriptStartTime &&
          currentTime <= line.transcriptEndTime
        ) {
          return line.id;
        }
      }
      return null;
    }, [lines, currentTime]);

    // PLAT-9408 parity for translated captions: progressive rendering.
    // Same pattern as TranslatedTranscriptView and _TranscriptionDiff —
    // mount enough lines to cover the playhead synchronously, then fill the
    // rest in rAF batches so toggling between views in a translated
    // language doesn't pay full O(N) layout on each mount.
    const RENDER_BATCH_SIZE = 200;
    const initialRenderCount = React.useMemo(() => {
      let cursorIdx = -1;
      if (typeof currentTime === 'number') {
        for (let i = 0; i < lines.length; i++) {
          const line = lines[i];
          if (
            currentTime >= line.transcriptStartTime &&
            currentTime <= line.transcriptEndTime
          ) {
            cursorIdx = i;
            break;
          }
        }
      }
      return Math.max(60, cursorIdx + 20);
    }, [lines, currentTime]);

    const [renderedCount, setRenderedCount] =
      React.useState(initialRenderCount);

    React.useEffect(() => {
      setRenderedCount(initialRenderCount);
    }, [lines, initialRenderCount]);

    React.useEffect(() => {
      if (renderedCount >= lines.length) return;
      const frame = requestAnimationFrame(() => {
        setRenderedCount(prev =>
          Math.min(prev + RENDER_BATCH_SIZE, lines.length),
        );
      });
      return () => cancelAnimationFrame(frame);
    }, [renderedCount, lines.length]);

    const visibleLines = React.useMemo(
      () => lines.slice(0, renderedCount),
      [lines, renderedCount],
    );

    React.useEffect(() => {
      if (!isPlaying || currentLineId === null) return;
      requestAnimationFrame(() => {
        // CLAUDE.md pitfall #12: scope to the LOCAL scrollable container
        // via `closest`. Multiple Transcript instances (Creator Studio +
        // clip-preview modal) both render `#transcript-scrollable-container`,
        // so a global `document.getElementById` would scroll the wrong
        // (hidden) instance — usually Creator Studio behind the modal.
        const container = containerRef.current?.closest(
          '#transcript-scrollable-container',
        ) as HTMLElement | null;
        if (!container) return;
        const el = container.querySelector(`[data-line-id='${currentLineId}']`);
        if (!el) return;
        const containerRect = container.getBoundingClientRect();
        const elRect = el.getBoundingClientRect();
        if (
          elRect.top >= containerRect.top &&
          elRect.bottom <= containerRect.bottom
        ) {
          return;
        }
        // PLAT-9646: scroll position centralized in `computePlayheadScrollTop`
        // (./utils). Single source of truth for all transcript views.
        const scrollTop = computePlayheadScrollTop(container, el);
        container.scrollTo({ top: scrollTop });
      });
    }, [currentLineId, isPlaying]);

    const breakEditingEnabled = !isPlaying && !!syntheticBreakHandlers;

    // Per-line word + phrase decomposition. Memoized off `visibleLines`
    // so cursor navigation doesn't pay this cost on every keystroke.
    const lineModels = React.useMemo(
      () => visibleLines.map(buildLineModel),
      [visibleLines],
    );

    // Flat list of every navigable cursor position across the visible
    // captions. One kind: 'in-line' between two adjacent words on
    // the same line. Line-gaps are NOT navigable stops — arrows flow
    // word-to-word across lines.
    const positions = React.useMemo<CursorPosition[]>(
      () => buildPositions(lineModels),
      [lineModels],
    );

    // Cursor index into `positions`. Null while playing (cursor
    // hidden during playback) or when there are no positions.
    const [cursorIdx, setCursorIdx] = React.useState<number | null>(null);
    // True after the user manually places the cursor (click or arrow)
    // since the last pause. While false, the cursor auto-tracks the
    // playhead so opening the captions panel parks the cursor at the
    // word being spoken. Resets to false on every pause so each pause
    // re-anchors the cursor to the current playhead.
    const [userMoved, setUserMoved] = React.useState(false);

    React.useEffect(() => {
      if (isPlaying || !breakEditingEnabled) {
        setCursorIdx(null);
        setUserMoved(false);
      }
    }, [isPlaying, breakEditingEnabled]);

    // Keep cursor in range as positions change (line edits, break
    // add/remove). Snap to the nearest valid index instead of dropping
    // the cursor entirely so the user keeps their place after an edit.
    React.useEffect(() => {
      if (cursorIdx === null) return;
      if (positions.length === 0) {
        setCursorIdx(null);
        return;
      }
      if (cursorIdx > positions.length - 1) {
        setCursorIdx(positions.length - 1);
      }
    }, [positions, cursorIdx]);

    // Auto-track the playhead while the user hasn't taken control. As
    // soon as the captions panel opens (or the video pauses again),
    // the cursor jumps to the boundary closest to the current playhead
    // time so the user always sees a cursor at the word being spoken.
    React.useEffect(() => {
      if (!breakEditingEnabled) return;
      if (isPlaying) return;
      if (userMoved) return;
      if (positions.length === 0) return;
      if (typeof currentTime !== 'number') {
        setCursorIdx(0);
        return;
      }
      let bestIdx = 0;
      let bestDelta = Math.abs(positions[0].time - currentTime);
      for (let i = 1; i < positions.length; i++) {
        const d = Math.abs(positions[i].time - currentTime);
        if (d < bestDelta) {
          bestIdx = i;
          bestDelta = d;
        }
      }
      setCursorIdx(bestIdx);
    }, [breakEditingEnabled, isPlaying, userMoved, positions, currentTime]);

    const containerRef = React.useRef<HTMLDivElement | null>(null);

    // Seek the video to the cursor's time on every USER-driven cursor
    // move so the preview frame follows the caption cursor. We skip
    // automatic playhead-tracking moves (`!userMoved`) — those would
    // re-seek the video to where it already is, with no benefit.
    // Skip while playing (video drives the cursor, not vice versa).
    const lastSeekedTimeRef = React.useRef<number | null>(null);
    React.useEffect(() => {
      if (isPlaying) return;
      if (!userMoved) return;
      if (!onSeekToTime) return;
      if (cursorIdx === null) return;
      const pos = positions[cursorIdx];
      if (!pos) return;
      if (lastSeekedTimeRef.current === pos.time) return;
      lastSeekedTimeRef.current = pos.time;
      onSeekToTime(pos.time);
    }, [cursorIdx, positions, isPlaying, userMoved, onSeekToTime]);

    // Bring the cursor's line into view when keyboard/click navigation
    // lands it outside the viewport. Scoped to the LOCAL scroll
    // container (via `closest`) so the clip-preview modal doesn't
    // scroll Creator Studio's transcript behind it (see CLAUDE.md
    // pitfall #12). Only fires for user-driven moves — auto-track
    // moves piggyback on the playhead's own scroll-into-view effect.
    React.useEffect(() => {
      if (!userMoved) return;
      if (cursorIdx === null) return;
      const pos = positions[cursorIdx];
      if (!pos) return;
      const scrollContainer = containerRef.current?.closest(
        '#transcript-scrollable-container',
      ) as HTMLElement | null;
      if (!scrollContainer) return;
      const lineEl = scrollContainer.querySelector(
        `[data-line-id='${pos.lineId}']`,
      );
      if (!lineEl) return;
      const containerRect = scrollContainer.getBoundingClientRect();
      const lineRect = lineEl.getBoundingClientRect();
      if (
        lineRect.top >= containerRect.top &&
        lineRect.bottom <= containerRect.bottom
      ) {
        return;
      }
      const scrollTop = computePlayheadScrollTop(scrollContainer, lineEl);
      scrollContainer.scrollTo({ top: scrollTop });
    }, [cursorIdx, positions, userMoved]);

    // Latest values for the keydown effect's closure. Storing in refs
    // lets the listener stay registered for the lifetime of engagement
    // without re-binding (and re-running stale callbacks) on each
    // cursor move. We MUST be sure the handler reads the freshest
    // `positions` and `cursorIdx` — the captions can change underneath.
    const positionsRef = React.useRef(positions);
    React.useEffect(() => {
      positionsRef.current = positions;
    }, [positions]);
    const cursorIdxRef = React.useRef(cursorIdx);
    React.useEffect(() => {
      cursorIdxRef.current = cursorIdx;
    }, [cursorIdx]);
    const handlersRef = React.useRef(syntheticBreakHandlers);
    React.useEffect(() => {
      handlersRef.current = syntheticBreakHandlers;
    }, [syntheticBreakHandlers]);
    const toggleSoftBreakAtRef = React.useRef(toggleSoftBreakAt);
    React.useEffect(() => {
      toggleSoftBreakAtRef.current = toggleSoftBreakAt;
    }, [toggleSoftBreakAt]);

    // Window-level capture-phase keydown handler. Capture phase fires
    // before bubble-phase listeners, so we beat the global
    // ShortcutProvider (which registers window keydown in bubble
    // phase) — its `timeline-stage` scope maps Arrow keys to frame
    // step and Delete to delete-active-element. Without this we'd
    // either scrub the playhead or delete a timeline element when
    // the user just wanted to walk caption words. Always registered
    // while paused + handlers wired so arrows / Enter work the moment
    // the panel opens — no click-to-engage required.
    React.useEffect(() => {
      if (!breakEditingEnabled) return;

      const onKeyDown = (e: KeyboardEvent) => {
        // Don't steal keys when the user is typing in a real input.
        const ae = document.activeElement;
        if (
          ae &&
          (ae.tagName === 'INPUT' ||
            ae.tagName === 'TEXTAREA' ||
            (ae instanceof HTMLElement && ae.isContentEditable))
        ) {
          return;
        }

        // Whitelist of keys this view owns. While the captions view is
        // mounted + paused + has handlers, ALWAYS consume these keys so
        // they never fall through to:
        //   - the global timeline-stage ShortcutProvider (Arrow → frame
        //     step, Delete/Backspace → delete-active-element), or
        //   - the Transcript scrollRef Enter handler, which would call
        //     `handleAddKaraokeBreak` against the original-language
        //     transcript cursor and clobber the translated clips
        //     (resulting karaoke clips lose `translatedText`, so the
        //     video captions overlay reverts to the source language).
        // Whether or not the cursor is actually positioned to take an
        // action, we still consume the key — silent no-op is better
        // than triggering the wrong command.
        const isOurKey =
          e.key === 'ArrowRight' ||
          e.key === 'ArrowLeft' ||
          e.key === 'ArrowUp' ||
          e.key === 'ArrowDown' ||
          e.key === 'Enter' ||
          e.key === 'Delete' ||
          e.key === 'Backspace';
        if (!isOurKey) return;

        e.preventDefault();
        e.stopImmediatePropagation();

        const positionsNow = positionsRef.current;
        const cursorNow = cursorIdxRef.current;
        const handlers = handlersRef.current;
        if (!handlers || positionsNow.length === 0) return;

        if (e.key === 'ArrowRight') {
          setCursorIdx(prev =>
            prev === null ? 0 : Math.min(prev + 1, positionsNow.length - 1),
          );
          setUserMoved(true);
          return;
        }
        if (e.key === 'ArrowLeft') {
          setCursorIdx(prev =>
            prev === null ? positionsNow.length - 1 : Math.max(prev - 1, 0),
          );
          setUserMoved(true);
          return;
        }
        if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
          // Up/Down: jump to the same `beforeWord` slot on the
          // previous/next caption row. If the target row has fewer
          // words, clamp to the row's last position. If we're on the
          // first/last row already, no-op (stay where we are).
          if (cursorNow === null) {
            setCursorIdx(0);
            setUserMoved(true);
            return;
          }
          const current = positionsNow[cursorNow];
          if (!current) return;
          const targetLineIdx =
            e.key === 'ArrowUp' ? current.lineIdx - 1 : current.lineIdx + 1;
          // Find the [first, last] index range of positions on the
          // target line. positions are emitted line-by-line and
          // beforeWord increases monotonically within each line, so
          // the ranges are contiguous.
          let firstIdx = -1;
          let lastIdx = -1;
          for (let i = 0; i < positionsNow.length; i++) {
            if (positionsNow[i].lineIdx === targetLineIdx) {
              if (firstIdx === -1) firstIdx = i;
              lastIdx = i;
            } else if (firstIdx !== -1) {
              break;
            }
          }
          if (firstIdx === -1) return; // no target line (top/bottom edge)
          const maxBeforeWord = positionsNow[lastIdx].beforeWord;
          const targetBeforeWord = Math.min(current.beforeWord, maxBeforeWord);
          setCursorIdx(firstIdx + targetBeforeWord);
          setUserMoved(true);
          return;
        }
        if (cursorNow === null) return;
        const pos = positionsNow[cursorNow];
        if (e.key === 'Enter' && e.shiftKey) {
          // PLAT-11914: a soft line break — same caption, next line.
          toggleSoftBreakAtRef.current(pos);
          return;
        }
        if (e.key === 'Enter') {
          // Always fire the synthetic break command — the command
          // itself validates (clip must exist, must be single-phrase,
          // textPosition must be in 0 < tp < text.length, split must
          // produce >= 2 sub-units). Letting the command's
          // authoritative check decide avoids us second-guessing edge
          // cases (e.g. phraseOriginalIndices not yet stamped on a
          // derived line).
          handlers.onAddBreak(
            pos.lineId,
            pos.phraseOriginalIndices,
            pos.textPosition,
          );
          return;
        }
        if (e.key === 'Backspace' && pos.softBreakBefore) {
          // PLAT-11914: at the start of a soft line, remove the soft break
          // — the caption is not merged with anything.
          toggleSoftBreakAtRef.current(pos);
          return;
        }
        if (e.key === 'Backspace') {
          // Backspace at the first word boundary of any non-first
          // line merges this line into the previous one. Works for
          // both synthetic-record sibling pairs (single-phrase) and
          // record-less splits (multi-phrase). The editor refuses
          // when the two clips don't share tokens (i.e. across a
          // natural producer boundary), keeping merge-only-undo-a-
          // split semantics.
          if (pos.beforeWord === 0 && pos.lineIdx > 0) {
            handlers.onMerge(pos.lineId);
          }
          return;
        }
        if (e.key === 'Delete') {
          // Delete at the last word boundary of any non-last line
          // merges THIS line with the next one. Symmetric counterpart
          // of Backspace.
          // The cursor is at the last in-line position when the next
          // entry in `positions` (if any) belongs to a later line.
          const next = positionsNow[cursorNow + 1];
          const isLastOnLine = !next || next.lineIdx !== pos.lineIdx;
          if (isLastOnLine && next) {
            handlers.onMerge(next.lineId);
          }
          return;
        }
      };

      window.addEventListener('keydown', onKeyDown, true);
      return () => window.removeEventListener('keydown', onKeyDown, true);
    }, [breakEditingEnabled]);

    const showCursorIdx = breakEditingEnabled ? cursorIdx : null;

    // Per-segment hover highlight: hovering a phrase span lights every
    // span that shares its `phraseKey`. Pieces of the same source phrase
    // split across lines via synthetic breaks share a key (rooted in
    // `phraseOriginalIndices`); independent phrases (different source
    // segments, multi-phrase lines) get unique keys and stay isolated.
    const [hoveredPhraseKey, setHoveredPhraseKey] = React.useState<
      string | null
    >(null);

    // Click handler factory for the inter-word hit-zone. Sets the
    // navigation cursor at the corresponding position and stops
    // propagation so the line's seek-onClick doesn't also fire.
    // Marks `userMoved` so subsequent playhead changes don't drag
    // the cursor away from where the user just placed it.
    const placeCursorAt = (idx: number) => (e: React.MouseEvent) => {
      e.stopPropagation();
      setCursorIdx(idx);
      setUserMoved(true);
    };

    // Click handler factory: clicking any word seeks to that word's
    // time AND, when break-editing is engaged, places the navigation
    // cursor at the START of the word. Bypasses `onLineClick` (which
    // would seek to the FIRST word of the line) so each word is its
    // own seek target.
    const placeCursorAtWordStart =
      (posIdx: number) => (e: React.MouseEvent) => {
        e.stopPropagation();
        const pos = positions[posIdx];
        if (!pos) return;
        if (onSeekToTime) onSeekToTime(pos.time);
        setCursorIdx(posIdx);
        setUserMoved(true);
      };

    // PLAT-11914: right-click a word — the menu offers a line break before
    // it while break editing is on, plus Fix Translation when that gesture
    // is on. `posIdx` is -1 for a line with no word slots.
    const handleCaptionWordContextMenu = (
      phrase: DerivedCaptionPhrase | null,
      e: React.MouseEvent<HTMLElement>,
      posIdx: number,
    ) => {
      const pos = positions[posIdx];
      if (breakEditingEnabled && pos && pos.beforeWord > 0) {
        e.preventDefault();
        e.stopPropagation();
        setCursorIdx(posIdx);
        setUserMoved(true);
        setLineBreakMenu({
          position: pos,
          positionXY: { x: e.clientX, y: e.clientY },
        });
      }
      if (editable) handleWordContextMenu(phrase, e);
    };
    const closeCaptionMenus = () => {
      setLineBreakMenu(null);
      if (contextMenu) handleMenuClose();
    };
    React.useEffect(() => {
      if (!breakEditingEnabled) setLineBreakMenu(null);
    }, [breakEditingEnabled]);

    return (
      <Container
        ref={containerRef}
        data-testid="translated-captions-view"
        onMouseLeave={() => setHoveredPhraseKey(null)}
      >
        {lineModels.map((model, idx) => {
          const { line } = model;
          if (model.words.length === 0 && !model.fallbackText) return null;

          const lineStart = line.transcriptStartTime;
          const lineEnd = line.transcriptEndTime;
          const isCurrent =
            typeof currentTime === 'number' &&
            currentTime >= lineStart &&
            currentTime <= lineEnd;
          const lineActive = isCurrent && isPlaying === true;
          const lineHighlight = lineActive && model.phrases.length === 0;

          const initialIndex = model.firstInitialIndex;

          // Find the in-line position (if any) that's anchored to this
          // line. Line-gaps are no longer in `positions` — arrows flow
          // word-to-word across lines as if there are no sentence
          // fragments (per PLAT-9623 simplification).
          const activePos =
            showCursorIdx !== null ? positions[showCursorIdx] : null;
          const activeInLine =
            activePos && activePos.lineIdx === idx ? activePos : null;

          // Index of the first navigable position on this line, used
          // to compute click-to-position offsets.
          const firstInLineIdx = positions.findIndex(p => p.lineIdx === idx);

          return (
            <Line
              key={line.id}
              data-testid="translated-captions-line"
              data-line-id={line.id}
              isHighlighted={lineHighlight}
              onClick={() => {
                if (initialIndex !== null) onLineClick?.(initialIndex);
              }}
            >
              <span className="line-text">
                {lineActive && model.phrases.length > 0
                  ? renderPlayingPhrases(
                      model,
                      currentTime,
                      hoveredPhraseKey,
                      setHoveredPhraseKey,
                    )
                  : renderWordsWithCursor(
                      model,
                      activeInLine ? activeInLine.beforeWord : null,
                      breakEditingEnabled
                        ? (beforeWord: number) =>
                            placeCursorAt(firstInLineIdx + beforeWord)
                        : null,
                      firstInLineIdx >= 0
                        ? (wi: number) =>
                            placeCursorAtWordStart(firstInLineIdx + wi)
                        : null,
                      hoveredPhraseKey,
                      setHoveredPhraseKey,
                      enableInlineSpanReplace,
                      // PLAT-10457 (A5): only attach the right-click handler when
                      // `editable` (flag on + paused + foreign language) or, since
                      // PLAT-11914, while line breaks can be edited. Off → no
                      // `onContextMenu`, so the native menu shows and there is
                      // no affordance — mirrors prose's "behaves as before" gate.
                      editable || breakEditingEnabled
                        ? (phrase, e, wi) =>
                            handleCaptionWordContextMenu(
                              phrase,
                              e,
                              wi === undefined || firstInLineIdx < 0
                                ? -1
                                : firstInLineIdx + wi,
                            )
                        : null,
                      // PLAT-10457 (A8): the once-per-render edited-phrase
                      // predicate (null when the flag is off / nothing edited) →
                      // words of an edited phrase get the `edited` class.
                      isCaptionWordEdited,
                    )}
              </span>
            </Line>
          );
        })}
        {/*
          PLAT-10457 (A5): the reused single-word "Fix Translation" menu +
          editor popover, wired to `usePhraseEditing` EXACTLY as the prose
          translated view (`TranscriptionTextTranslated`) — same handlers, same
          derived values. Commit path is `handleSave` → `editTranslatedPhrase`
          (dual-writes integer + element-id keys); revert is `handleRevert`. The
          source-text + source-language rows read the SHARED store (clip-proxied
          transcription for the source elements, the whole-story translation for
          the language), mirroring prose.
        */}
        {(contextMenu || lineBreakMenu) && (
          <TranslatedPhraseContextMenu
            positionXY={
              contextMenu?.positionXY ??
              lineBreakMenu?.positionXY ?? { x: 0, y: 0 }
            }
            onEdit={
              contextMenu
                ? () => {
                    setLineBreakMenu(null);
                    handleMenuEdit();
                  }
                : undefined
            }
            onClose={closeCaptionMenus}
            lineBreak={
              lineBreakMenu
                ? {
                    hasBreak: lineBreakMenu.position.softBreakBefore != null,
                    onToggle: () => {
                      toggleSoftBreakAt(lineBreakMenu.position);
                      closeCaptionMenus();
                    },
                  }
                : undefined
            }
          />
        )}
        {editing && (
          <TranslatedPhraseEditorPopover
            anchorElement={editing.anchorElement}
            sourceText={resolveSourceText(
              editing.phrase,
              transcriptionStore.getFinalTranscriptionElements() ?? [],
            )}
            sourceLanguageLabel={
              translation?.sourceLanguage
                ? formatLanguageCaption(translation.sourceLanguage)
                : undefined
            }
            originalValue={editingOriginalValue}
            currentValue={editingCurrentValue}
            onSave={value => handleSave(editing.phrase, value)}
            onCancel={handleCancel}
            canRevert={editingIsEdited}
            onRevert={() => handleRevert(editing.phrase)}
          />
        )}
        {/*
          PLAT-10457 (A4): mount the shipped multi-word span-replace affordance,
          scoped to THIS view's local `containerRef` (never a global `document`
          query — CLAUDE.md pitfall #13, two `#transcript-scrollable-container`
          coexist behind the clip-preview modal). Mirrors the prose mount
          (`TranscriptionTextTranslated`). PAUSED-ONLY (DESIGN D-a v1): while
          playing, the caption cursor / word highlight own the view, so the
          drag-select affordance stays off. Gated on `language` too because the
          reused layer requires a non-null language (this view's prop is
          nullable); an empty/absent language means there is nothing to fix.
        */}
        {enableInlineSpanReplace && !isPlaying && language && (
          <InlineSpanReplaceLayer
            containerRef={containerRef}
            language={language}
          />
        )}
      </Container>
    );
  },
);

// Phrase-identity key for hover highlighting. Resolves the SOURCE
// translation phrase (the indivisible unit the translator emitted),
// so hovering any word lights every other word that came from the
// same source phrase — even when the phrase has been split across
// multiple rows by synthetic breaks, or when a row contains multiple
// independent phrases that should NOT highlight together.
//
// Three tiers, narrowest first:
//   1. The phrase entry's own `originalIndices` (set on every
//      translatedPhrase by the producer, preserved by Add/Merge
//      commands). This is the authoritative key.
//   2. Line-level `phraseOriginalIndices` for single-phrase lines
//      that didn't get per-entry originalIndices (e.g. legacy clips
//      produced before the field existed).
//   3. `ln:<lineId>:<phraseIdx>` fallback — each phrase span stays
//      independent when no source identity is available.
function computePhraseKey(
  line: KaraokeLine,
  phrase: { originalIndices?: number[] } | null,
  phraseIdx: number,
  phrasesLength: number,
): string {
  if (phrase?.originalIndices && phrase.originalIndices.length > 0) {
    return `pi:${phrase.originalIndices.join(',')}`;
  }
  if (
    phrasesLength <= 1 &&
    line.phraseOriginalIndices &&
    line.phraseOriginalIndices.length > 0
  ) {
    return `pi:${line.phraseOriginalIndices.join(',')}`;
  }
  return `ln:${line.id}:${phraseIdx}`;
}

// ---------------------------------------------------------------------------
// Per-line decomposition + flat cursor-position model.
// ---------------------------------------------------------------------------

interface WordToken {
  value: string;
  // Char offset (inclusive) into the line's flat text — used as the
  // `textPosition` argument to `onAddBreak` for the boundary AFTER this
  // word. Only meaningful on single-phrase lines.
  startOffset: number;
  endOffset: number;
  // Index into `phrases` identifying which phrase this word came from.
  // Used to gate "this Enter is a synthetic break" vs other cases.
  phraseIdx: number;
  // Char offsets relative to the WORD'S phrase text (not the line text).
  // Used to compute the synthetic time at this word's boundary via
  // `computeProportionAtTextPosition` against the phrase's
  // [ts, endTs] window.
  startInPhrase: number;
  endInPhrase: number;
  // PLAT-11835 — whether a space renders before this word. Offsets keep one
  // char per phrase boundary (the frame `AddSyntheticCaptionBreakCommand`
  // walks), so an offset gap alone can't tell a real space from a spaceless
  // Japanese phrase boundary.
  spaceBefore: boolean;
  // PLAT-11914 — the user soft line break that starts a new line at this
  // word, as stored (its offset may sit before the word's start), or null.
  softBreakBefore: PhraseSoftBreak | null;
}

interface LineModel {
  line: KaraokeLine;
  words: WordToken[];
  // Phrase-level units passed through for highlight rendering during
  // playback. Empty when the underlying line has no `translatedPhrases`
  // (legacy path with only `translatedText`). Each entry carries the
  // source-phrase identity (`originalIndices`) when available — used
  // by `computePhraseKey` to light all pieces of the same source
  // phrase together on hover — plus A2's additive in-memory lead
  // identity (`leadUuid` / `initialIndex`), which A3 stamps on the word
  // spans (`DerivedCaptionPhrase`).
  phrases: readonly DerivedCaptionPhrase[];
  // The line text used when no phrase data exists (legacy fallback).
  fallbackText: string;
  firstInitialIndex: number | null;
}

function buildLineModel(line: KaraokeLine): LineModel {
  // A2 (PLAT-10457) derives each caption phrase WITH its lead identity
  // (`leadUuid` / `initialIndex`) as additive IN-MEMORY fields — never
  // persisted, so the shared `KaraokeLine.translatedPhrases` type deliberately
  // omits them (the "not-persisted" invariant). Read them typed via the
  // exported `DerivedCaptionPhrase` with a narrow cast at this boundary; A3
  // stamps them on the word spans (AC2). No persisted-type widening, no `any`.
  const phrases = (line.translatedPhrases ??
    []) as readonly DerivedCaptionPhrase[];
  const fallbackText =
    line.translatedText ??
    line.transcriptTokens
      .filter(t => t.type === 'text' && t.value)
      .map(t => t.value)
      .join(' ');

  // Build the line's flat text by concatenating phrase texts with one
  // joining char per boundary — the offset frame the break command walks,
  // whether or not a space renders there (`spaceBefore`). Track each
  // phrase's char offset window so word offsets can roll up to a
  // line-relative `textPosition`.
  const words: WordToken[] = [];
  let cursorOffset = 0;
  if (phrases.length > 0) {
    for (let pi = 0; pi < phrases.length; pi++) {
      const phrase = phrases[pi];
      const text = phrase.text;
      // PLAT-11481 — locale-aware words (shared tokenizer), so spaceless
      // Japanese phrases expose real slots for cursor movement and manual
      // line-break placement; identical to the old /\S+/ walk for
      // whitespace text (pieces reconstruct the text exactly).
      let inPhrase = 0;
      let firstInPhrase = true;
      for (const piece of tokenizePhraseWords(text).pieces) {
        if (piece.trim().length > 0) {
          // PLAT-11836 — one slot per character inside a spaceless-script
          // word, so a Japanese line can break anywhere, not only where the
          // dictionary tokenizer happened to end a word. Latin words stay
          // whole (`[piece]`).
          for (const unit of splitSpacelessScriptCharacters(piece)) {
            const prev = words[words.length - 1];
            words.push({
              value: unit,
              startOffset: cursorOffset + inPhrase,
              endOffset: cursorOffset + inPhrase + unit.length,
              phraseIdx: pi,
              startInPhrase: inPhrase,
              endInPhrase: inPhrase + unit.length,
              // Inside a phrase, replay the source whitespace; at a phrase
              // boundary, space it the way the caption-row join does
              // (PLAT-11835: never between Japanese phrases).
              spaceBefore:
                !!prev &&
                (firstInPhrase
                  ? needsSpaceBetweenCaptionRows(
                      phrases[prev.phraseIdx].text,
                      text,
                    )
                  : prev.endOffset < cursorOffset + inPhrase),
              softBreakBefore: null,
            });
            firstInPhrase = false;
            inPhrase += unit.length;
          }
        } else {
          inPhrase += piece.length;
        }
      }
      cursorOffset += text.length;
      if (pi < phrases.length - 1) cursorOffset += 1; // joining space
    }
  } else if (fallbackText) {
    // PLAT-11481 — same locale-aware word slots as the phrase branch.
    let offset = 0;
    for (const piece of tokenizePhraseWords(fallbackText).pieces) {
      if (piece.trim().length > 0) {
        for (const unit of splitSpacelessScriptCharacters(piece)) {
          const prev = words[words.length - 1];
          words.push({
            value: unit,
            startOffset: offset,
            endOffset: offset + unit.length,
            phraseIdx: 0,
            startInPhrase: offset,
            endInPhrase: offset + unit.length,
            spaceBefore: !!prev && prev.endOffset < offset,
            softBreakBefore: null,
          });
          offset += unit.length;
        }
      } else {
        offset += piece.length;
      }
    }
  }

  if (phrases.length > 0) {
    markSoftBreakWords(words, line.softBreakPhrasePositions ?? []);
  }

  const firstText = line.transcriptTokens.find(t => t.type === 'text');
  const firstInitialIndex = firstText?.initial_index ?? null;

  return {
    line,
    words,
    phrases,
    fallbackText,
    firstInitialIndex,
  };
}

/**
 * PLAT-11914 — flag the word each soft break starts a line at: the first
 * word of the break's phrase at or after its offset. Not the line's first
 * word — the caption already starts a line there.
 */
function markSoftBreakWords(
  words: WordToken[],
  breaks: readonly PhraseSoftBreak[],
): void {
  for (const b of breaks) {
    const wi = words.findIndex(
      w => w.phraseIdx === b.phraseIndex && w.startInPhrase >= b.offset,
    );
    const target =
      wi >= 0 ? wi : words.findIndex(w => w.phraseIdx > b.phraseIndex);
    if (target > 0) words[target].softBreakBefore ??= b;
  }
}

/**
 * PLAT-11914 — a phrase's text cut at its soft breaks, one entry per line,
 * for the read-only playing render.
 */
function splitPhraseTextAtSoftBreaks(
  text: string,
  phraseIndex: number,
  breaks: readonly PhraseSoftBreak[],
): string[] {
  const offsets = breaks
    .filter(b => b.phraseIndex === phraseIndex && b.offset > 0)
    .map(b => b.offset)
    .sort((a, b) => a - b);
  const lines: string[] = [];
  let from = 0;
  for (const offset of offsets) {
    lines.push(text.slice(from, offset).trimEnd());
    from = offset;
  }
  lines.push(text.slice(from).trimStart());
  return lines;
}

interface InLinePosition {
  kind: 'in-line';
  lineIdx: number;
  lineId: string;
  // 0-based word index BEFORE which the cursor sits. beforeWord=0 is
  // the very start of the line (before the first word); beforeWord=w
  // for w>0 is between word w-1 and word w.
  beforeWord: number;
  // textPosition (char offset) within the line's single phrase. 0 at
  // line start (no break possible there).
  textPosition: number;
  phraseOriginalIndices: number[];
  // PLAT-11914 — the same slot as a soft-break anchor: which of the line's
  // phrases, and the offset inside it; and the soft break there, if any.
  phraseIndex: number;
  offsetInPhrase: number;
  softBreakBefore: PhraseSoftBreak | null;
  // Video time corresponding to this cursor position. Resolved via the
  // same SplitStrategy used for synthetic breaks (proportional split
  // within the phrase's [ts, endTs] window) so seeking to this time
  // lands the preview frame at the same instant the user would get
  // from pressing Enter at this position.
  time: number;
}

type CursorPosition = InLinePosition;

function buildPositions(lineModels: LineModel[]): CursorPosition[] {
  // Emit one in-line position BEFORE each word — line-gaps are NOT
  // navigable stops. Arrows flow word-to-word across lines as if
  // there are no sentence fragments. Removal of breaks happens at
  // line-edge positions (Backspace at first-of-line, Delete at
  // last-of-line) — those resolve to the merge command, which figures
  // out the matching break record itself.
  const out: InLinePosition[] = [];
  for (let li = 0; li < lineModels.length; li++) {
    const model = lineModels[li];
    if (model.words.length === 0) continue;
    const phraseOriginalIndices = model.line.phraseOriginalIndices;

    for (let wi = 0; wi < model.words.length; wi++) {
      const word = model.words[wi];
      const phrase = model.phrases[word.phraseIdx];
      // Time = start of this word, computed from the karaoke word-
      // timing algorithm (proportion at word.startInPhrase within
      // the phrase's [ts, endTs] window). At beforeWord=0 of a
      // single-phrase line this resolves to phrase.ts (line start),
      // exactly what the user expects when opening the captions.
      let time = model.line.transcriptStartTime;
      if (phrase) {
        const proportion = computeProportionAtTextPosition(
          phrase.text,
          word.startInPhrase,
          DEFAULT_SPLIT_STRATEGY,
        );
        time = phrase.ts + proportion * (phrase.endTs - phrase.ts);
      }
      // textPosition (for Enter → split) sits at word.startOffset in
      // line-relative coords. The Add command itself validates that
      // the resulting split is non-empty.
      out.push({
        kind: 'in-line',
        lineIdx: li,
        lineId: model.line.id,
        beforeWord: wi,
        textPosition: word.startOffset,
        phraseOriginalIndices: phraseOriginalIndices ?? [],
        phraseIndex: word.phraseIdx,
        offsetInPhrase: word.startInPhrase,
        softBreakBefore: word.softBreakBefore,
        time,
      });
    }
  }

  return out;
}

// ---------------------------------------------------------------------------
// Per-line rendering helpers.
// ---------------------------------------------------------------------------

function renderPlayingPhrases(
  model: LineModel,
  currentTime: number | undefined,
  hoveredPhraseKey: string | null,
  setHoveredPhraseKey: (k: string | null) => void,
) {
  const { line, phrases } = model;
  return phrases.map((p, i) => {
    const active =
      typeof currentTime === 'number' &&
      currentTime >= p.ts &&
      currentTime < p.endTs;
    const key = computePhraseKey(line, p, i, phrases.length);
    return (
      <React.Fragment key={`${line.id}-${i}`}>
        <Phrase
          isHighlighted={active}
          hovered={hoveredPhraseKey === key}
          onMouseEnter={() => setHoveredPhraseKey(key)}
        >
          {splitPhraseTextAtSoftBreaks(
            p.text,
            i,
            line.softBreakPhrasePositions ?? [],
          ).map((text, li) => (
            <React.Fragment key={li}>
              {li > 0 && <br />}
              {text}
            </React.Fragment>
          ))}
        </Phrase>
        {i < phrases.length - 1 &&
        needsSpaceBetweenCaptionRows(p.text, phrases[i + 1].text)
          ? ' '
          : null}
      </React.Fragment>
    );
  });
}

function renderWordsWithCursor(
  model: LineModel,
  cursorBeforeWord: number | null,
  onClickBoundary:
    | ((beforeWord: number) => (e: React.MouseEvent) => void)
    | null,
  onClickWord: ((wi: number) => (e: React.MouseEvent) => void) | null,
  hoveredPhraseKey: string | null,
  setHoveredPhraseKey: (k: string | null) => void,
  // PLAT-10457 (A3): stamp source-phrase identity on the word spans when the
  // Fix-Translation gesture is enabled. `phrase.initialIndex` is always present
  // on derived phrases (the integer lead), so `data-initial-index` renders even
  // for legacy id-less data; `phrase.leadUuid` is undefined there, so React
  // OMITS `data-source-uuid` — mirroring the prose stamp exactly.
  stampIdentity: boolean,
  // PLAT-10457 (A5): right-click a caption word to open the reused
  // Fix-Translation menu. Null when not `editable` (flag off / playing / no
  // language) → no `onContextMenu` attached, so the native menu shows and there
  // is no affordance. The handler resolves the word's `DerivedCaptionPhrase` to
  // its store phrase and opens the menu (mirrors the prose word's onContextMenu).
  onWordContextMenu:
    | ((
        phrase: DerivedCaptionPhrase | null,
        e: React.MouseEvent<HTMLElement>,
        // PLAT-11914: the word's index in the line; absent on a line with no
        // word slots.
        wi?: number,
      ) => void)
    | null,
  // PLAT-10457 (A8): O(1) predicate — true when the word's `DerivedCaptionPhrase`
  // resolves to an EDITED store phrase. Null when the changed-highlight flag is
  // off (or nothing is edited); then no word carries the `edited` class and the
  // shared `changedPhraseHighlightCss` on `Word` never matches (byte-identical to
  // today). Computed ONCE per render by the caller — never an O(N) scan per word.
  isEditedPhrase: ((phrase: DerivedCaptionPhrase | null) => boolean) | null,
) {
  if (model.words.length === 0) {
    const fallbackPhrase = model.phrases[0] ?? null;
    const key = computePhraseKey(
      model.line,
      fallbackPhrase,
      0,
      model.phrases.length,
    );
    return (
      <Word
        hovered={hoveredPhraseKey === key}
        onMouseEnter={() => setHoveredPhraseKey(key)}
        onContextMenu={
          onWordContextMenu
            ? e => onWordContextMenu(fallbackPhrase, e)
            : undefined
        }
        className={isEditedPhrase?.(fallbackPhrase) ? 'edited' : undefined}
        data-initial-index={
          stampIdentity ? fallbackPhrase?.initialIndex : undefined
        }
        data-source-uuid={stampIdentity ? fallbackPhrase?.leadUuid : undefined}
      >
        {model.fallbackText}
      </Word>
    );
  }
  const out: React.ReactNode[] = [];
  const phrasesLength = model.phrases.length;
  for (let wi = 0; wi < model.words.length; wi++) {
    const w = model.words[wi];
    const isActive = cursorBeforeWord === wi;
    const phrase = model.phrases[w.phraseIdx] ?? null;
    const key = computePhraseKey(
      model.line,
      phrase,
      w.phraseIdx,
      phrasesLength,
    );
    // Cursor slot BEFORE this word. For wi=0 (line start) there's no
    // leading space — the cursor sits flush at the line's left edge.
    // For wi>0 we render a normal inter-word space first, then the
    // cursor slot, so the cursor sits at the START of the next word
    // (one space's worth of room follows the previous word). The
    // cursor slot is always present (transparent when inactive) so
    // toggling the cursor across boundaries doesn't shift layout.
    // PLAT-11914: a soft line break starts a new line inside the same row.
    if (w.softBreakBefore) out.push(<br key={`br-${wi}`} />);
    out.push(
      <Boundary
        key={`b-${wi}`}
        onMouseDown={e => {
          // Don't kick off a text-selection drag from a boundary
          // click — we want the click to be a clean cursor place.
          e.preventDefault();
        }}
        onClick={onClickBoundary ? onClickBoundary(wi) : undefined}
        interactive={!!onClickBoundary}
      >
        {/* PLAT-11481 / PLAT-11835 — render the inter-word space only where
            the text has one: Japanese words and phrases are adjacent, and
            injecting a space here re-created the stray-space defect
            PLAT-11468 removed. The boundary span (with its cursor slot)
            stays as the click target either way. */}
        {w.spaceBefore && !w.softBreakBefore ? ' ' : null}
        <InLineCursor active={isActive} tight={wi > 0 && !w.spaceBefore} />
      </Boundary>,
    );
    out.push(
      <Word
        key={`w-${wi}`}
        hovered={hoveredPhraseKey === key}
        onMouseEnter={() => setHoveredPhraseKey(key)}
        onClick={onClickWord ? onClickWord(wi) : undefined}
        onContextMenu={
          onWordContextMenu ? e => onWordContextMenu(phrase, e, wi) : undefined
        }
        className={isEditedPhrase?.(phrase) ? 'edited' : undefined}
        data-initial-index={stampIdentity ? phrase?.initialIndex : undefined}
        data-source-uuid={stampIdentity ? phrase?.leadUuid : undefined}
      >
        {w.value}
      </Word>,
    );
  }
  return out;
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  font-size: ${CAPTION_LINE_FONT_SIZE};
  line-height: ${CAPTION_LINE_LINE_HEIGHT};
  padding-bottom: 16px;
  outline: none;
`;

// Per-segment hover highlight color. A "segment" is a phrase as the
// user perceives it: a contiguous translated chunk that belongs to one
// source phrase. Hovering any piece of a segment lights every piece
// (including pieces split across lines via synthetic breaks); other
// segments stay independent.
const SEGMENT_HIGHLIGHT_COLOR = 'lightgreen';

const Phrase = styled.span<{ isHighlighted: boolean; hovered: boolean }>`
  background-color: ${p => (p.isHighlighted ? '#dfb615' : 'transparent')};
  color: ${p =>
    p.isHighlighted
      ? 'black'
      : p.hovered
        ? SEGMENT_HIGHLIGHT_COLOR
        : 'inherit'};
  border-radius: 2px;
  padding: 0 1px;
`;

const Line = styled.div<{
  isHighlighted: boolean;
}>`
  cursor: pointer;
  padding: ${CAPTION_LINE_VERTICAL_PADDING} 0;
  &:not(:last-child) {
    border-bottom: 1px solid ${CAPTION_LINE_DIVIDER_COLOR};
  }
  color: ${p => (p.isHighlighted ? 'black' : 'var(--text-secondary)')};
  & > .line-text {
    background-color: ${p => (p.isHighlighted ? '#dfb615' : 'transparent')};
    border-radius: var(--radius);
    box-decoration-break: clone;
    -webkit-box-decoration-break: clone;
  }
`;

// PLAT-10457 (A8): interpolate the SHARED changed-phrase green rule (imported
// verbatim from changedPhraseKit — do NOT copy it). It fires only when a word
// carries the `edited` class (added above only when the flag is on AND the phrase
// is edited). The captions `Word` never gets `.current`/`.cursor-before` — the
// playhead is a separate `InLineCursor`/`Boundary`, not a class on the word — so
// the rule's `&.edited:not(.current)` selector matches whenever `.edited` is set.
const Word = styled.span<{ hovered: boolean }>`
  color: ${p => (p.hovered ? SEGMENT_HIGHLIGHT_COLOR : 'inherit')};
  cursor: text;
  ${changedPhraseHighlightCss}
`;

// Inter-word hit zone. Always renders space + 2px cursor slot, so
// active/inactive boundaries occupy identical width — clicking
// anywhere in the slot places the cursor without layout shift.
const Boundary = styled.span<{ interactive: boolean }>`
  cursor: ${p => (p.interactive ? 'text' : 'inherit')};
  white-space: pre;
`;

// Cursor slot between the inter-word space and the next word. Always
// occupies 2px of width to keep layout stable; turns yellow + blinks
// when active so the cursor visually sits at the START of the next
// word (which is what the user expects). A `tight` slot sits between two
// adjacent characters with no space (PLAT-11836: every Japanese character
// is a slot) — it takes no layout width, or the 2px per slot would letter-
// space the line, and the cursor overlaps the neighbouring glyphs by 1px.
const InLineCursor = styled.span<{ active: boolean; tight?: boolean }>`
  display: inline-block;
  width: 2px;
  ${p => p.tight && 'margin: 0 -1px;'}
  height: 1em;
  vertical-align: text-bottom;
  background-color: ${p => (p.active ? '#dfb615' : 'transparent')};
  border-radius: 1px;
  ${p => p.active && 'animation: blink 1s steps(2) infinite;'}
  @keyframes blink {
    50% {
      opacity: 0;
    }
  }
`;
